import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ButtonRadiusEnum } from '../../shared/components/button/types/button-radius.enum';
import { ButtonSizeEnum } from '../../shared/components/button/types/button-size.enum';
import { ButtonTypeEnum } from '../../shared/components/button/types/button-type.enum';
import { InputComponent } from '../../shared/components/input/input.component';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { TrackTableComponent } from '../../shared/components/track-table/track-table.component';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { TrackService } from '../../shared/services/track/track.service';
import { Track } from '../../shared/models/interfaces/track.interface';
import { Sorts } from '../../shared/services/pagination/models/typies/sorts.type';
import { ModalAction } from '../../shared/modals/modal-actions/modal-action.interface';
import { PaginationDto } from '../../shared/services/pagination/models/interfaces/pagination-dto';
import { CrudRequestParams } from '../../shared/services/pagination/models/interfaces/crud';
import { ModalService } from '../../shared/services/modal/modal.service';
import { createCancelConfirmButtons, openConfirmModal } from '../../helpers/modal.helper';
import { CreateTrackModalComponent, CreateTrackModalResult } from './modals/create-track-modal.component';
import { SuccessModalComponent } from '../../shared/modals/success-modal/success-modal.component';
import { ToastService } from '../../shared/components/toast/service/toast.service';
import { getCrudErrorMessage } from '../../shared/utils/functions';

@Component({
  selector: 'l-track',
  imports: [
    IconComponent,
    ButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    TrackTableComponent,
  ],
  templateUrl: './track.component.html',
})
export class TrackComponent implements OnInit, OnDestroy {

  constructor(
    public readonly trackService: TrackService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
  ) {}

  public readonly buttonTypeEnum = ButtonTypeEnum;

  public readonly buttonSizeEnum = ButtonSizeEnum;

  public readonly buttonRadiusEnum = ButtonRadiusEnum;

  public readonly searchControl = new FormControl('');

  public isLoading = false;

  public limit = 10;

  public page = 1;

  public sort: Sorts = { field: 'updatedAt', order: 'DESC' };

  public source: PaginationDto<Track> = {
    data: [],
    page: 1,
    pageCount: 0,
    count: 0,
    total: 0,
  };

  private readonly destroy$$ = new Subject<void>();

  private readonly modalWidth = '700px';

  public get tracks(): Track[] {
    return this.source.data;
  }

  public get total(): number {
    return this.source.total;
  }

  public async ngOnInit(): Promise<void> {
    this.searchControl.valueChanges.pipe(
      debounceTime(400),
      takeUntil(this.destroy$$),
    ).subscribe((value) => {
      void this.loadTracks(1, value || '');
    });

    await this.loadTracks(1);
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  public openCreateTrackModal(): void {
    const ref = this.modalService.open(CreateTrackModalComponent, {
      width: this.modalWidth,
      modal: true,
      closable: false,
      showHeader: false,
      data: {
        mode: 'create',
      },
    });

    ref.onClose.subscribe((result: CreateTrackModalResult | undefined) => {
      if (!result) {
        return;
      }

      void this.handleTrackSaved(result);
    });
  }

  public async onChangePage(page: number): Promise<void> {
    await this.loadTracks(page);
  }

  public async onChangeSort(sort?: Sorts): Promise<void> {
    this.sort = sort || { field: 'updatedAt', order: 'DESC' };
    await this.loadTracks(1);
  }

  public getRowActions(track: Track): ModalAction[] {
    const isActive = track.isActive;

    return [
      {
        label: 'Editar',
        icon: 'edit',
        callback: () => this.openEditTrackModal(track),
      },
      {
        label: isActive ? 'Desativar' : 'Ativar',
        icon: 'power_settings_new',
        callback: () => this.openTrackStatusModal(track),
      },
      {
        label: 'Excluir',
        icon: 'delete',
        colorClass: 'text-lDanger',
        callback: () => this.openTrackDeleteModal(track),
      },
    ];
  }

  private async loadTracks(page: number, search?: string): Promise<void> {
    try {
      this.isLoading = true;

      const searchValue = (search ?? this.searchControl.value ?? '').trim();

      const params: CrudRequestParams<Track> = {
        page,
        limit: this.limit,
        sort: [{ field: this.sort.field, order: this.sort.order }],
      };

      if (searchValue) {
        params.search = {
          $or: [
            { name: { $contL: searchValue } },
            { description: { $contL: searchValue } },
          ],
        };
      }

      this.source = await this.trackService.getMany(params);

      this.page = this.source.page;
    } finally {
      this.isLoading = false;
    }
  }

  private openTrackStatusModal(track: Track): void {
    const actionLabel = track.isActive ? 'Desativar' : 'Ativar';
    const nextIsActive = !track.isActive;

    openConfirmModal(
      this.modalService,
      this.modalWidth,
      {
        title: `${actionLabel} trilha`,
        description: `Tem certeza que deseja ${actionLabel.toLowerCase()} esta trilha?`,
        buttons: createCancelConfirmButtons(
          actionLabel,
          track.isActive ? ButtonTypeEnum.PRIMARY : ButtonTypeEnum.SUCCESS,
        ),
      },
      async () => {
        await this.updateTrackStatus(track, nextIsActive);
      },
      this.destroy$$,
    );
  }

  private openTrackDeleteModal(track: Track): void {
    openConfirmModal(
      this.modalService,
      this.modalWidth,
      {
        title: 'Excluir trilha',
        description: `Tem certeza que deseja excluir a trilha "${track.name}"?`,
        buttons: createCancelConfirmButtons('Excluir', ButtonTypeEnum.DANGER),
      },
      async () => {
        await this.deleteTrack(track);
      },
      this.destroy$$,
    );
  }

  private async updateTrackStatus(track: Track, isActive: boolean): Promise<void> {
    try {
      await this.trackService.setActive(track.id, isActive);
      await this.loadTracks(this.page);
      this.toastService.success(isActive ? 'Trilha ativada com sucesso.' : 'Trilha desativada com sucesso.');
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    }
  }

  private async deleteTrack(track: Track): Promise<void> {
    try {
      await this.trackService.delete(track.id);
      await this.loadTracks(this.page);
      this.toastService.success('Trilha excluída com sucesso.');
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    }
  }

  private openEditTrackModal(track: Track): void {
    const coursesFromRelation = track.courses?.map(course => course.id).filter(Boolean) ?? [];
    const coursesIds = coursesFromRelation.length ? coursesFromRelation : (track.coursesIds ?? []);

    const ref = this.modalService.open(CreateTrackModalComponent, {
      width: this.modalWidth,
      modal: true,
      closable: false,
      showHeader: false,
      data: {
        mode: 'edit',
        trackId: track.id,
        name: track.name,
        coursesIds,
        coverImageUrl: track.coverImageUrl ?? '',
        description: track.description ?? '',
        shortDescription: track.shortDescription ?? '',
      },
    });

    ref.onClose.subscribe((result: CreateTrackModalResult | undefined) => {
      if (!result) {
        return;
      }

      void this.handleTrackSaved(result);
    });
  }

  private async handleTrackSaved(result: CreateTrackModalResult): Promise<void> {
    await this.loadTracks(this.page);

    this.modalService.open(SuccessModalComponent, {
      width: this.modalWidth,
      modal: true,
      closable: false,
      showHeader: false,
      data: {
        title: result.mode === 'create' ? 'Trilha criada com sucesso!' : 'Trilha editada com sucesso!',
        subtitle: 'Você pode visualizar ou editar através da lista de trilhas',
        imageAlt: result.mode === 'create' ? 'Trilha criada com sucesso' : 'Trilha editada com sucesso',
      },
    });
  }

}
